import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { PatientResponse } from '../models/patient.model';
import { PatientsService } from '../services/patients.service';
import { PatientModalComponent } from './patient-modal/patient-modal.component';
import { PatientSummaryComponent } from './patient-summary/patient-summary.component';

interface PatientGroup {
  label: string;
  patients: PatientResponse[];
}

@Component({
  selector: 'app-patients-workspace',
  standalone: true,
  imports: [CommonModule, FormsModule, PatientModalComponent, PatientSummaryComponent],
  templateUrl: './patients-workspace.component.html',
  styleUrl: './patients-workspace.component.scss',
})
export class PatientsWorkspaceComponent implements OnInit, OnDestroy {
  private readonly patientsService = inject(PatientsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly patients = signal<PatientResponse[]>([]);
  readonly selectedMrn = signal<string | null>(null);
  readonly activeOnly = signal(false);
  readonly searchOpen = signal(false);
  readonly searchText = signal('');

  @ViewChild(PatientSummaryComponent) private patientSummary?: PatientSummaryComponent;

  displayPatientModal = false;

  readonly filteredPatients = computed(() => {
    let list = this.patients();
    if (this.activeOnly()) {
      list = list.filter((p) => p.active);
    }
    const q = this.searchText().trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.toLowerCase();
        const mrn = (p.mrn ?? '').toLowerCase();
        return name.includes(q) || mrn.includes(q);
      });
    }
    return [...list].sort((a, b) => this.lastVisitTime(b) - this.lastVisitTime(a));
  });

  readonly groups = computed((): PatientGroup[] => {
    const byDay = new Map<string, PatientResponse[]>();
    for (const p of this.filteredPatients()) {
      const key = this.dayKey(p);
      const bucket = byDay.get(key) ?? [];
      bucket.push(p);
      byDay.set(key, bucket);
    }
    return [...byDay.entries()].map(([label, patients]) => ({ label, patients }));
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      this.selectedMrn.set(id);
    });
    this.loadPatients();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectPatient(patient: PatientResponse): void {
    const mrn = patient.mrn;
    if (!mrn) {
      return;
    }
    this.selectedMrn.set(mrn);
    void this.router.navigate(['/patients', mrn]);
  }

  onMobileBack(): void {
    this.selectedMrn.set(null);
    void this.router.navigate(['/patients']);
  }

  showAddPatientModal(): void {
    this.displayPatientModal = true;
  }

  onBarNewVisit(): void {
    this.patientSummary?.onNewVisit();
  }

  onPatientSaved(patient: PatientResponse): void {
    this.displayPatientModal = false;
    this.loadPatients();
    if (patient?.mrn) {
      this.selectedMrn.set(patient.mrn);
      void this.router.navigate(['/patients', patient.mrn]);
    }
  }

  patientLabel(patient: PatientResponse): string {
    const name = `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim();
    return name || 'Unknown Patient';
  }

  metaLine(patient: PatientResponse): string {
    const mrn = patient.mrn ? `MRN ${patient.mrn}` : 'No MRN';
    const visit = this.getLastVisitDate(patient);
    return `${mrn} · ${visit}`;
  }

  getLastVisitDate(patient: PatientResponse): string {
    const lv = patient.lastVisit;
    if (lv !== undefined && lv !== null && String(lv).trim() !== '') {
      return this.formatDateMdYyyy(String(lv));
    }
    return 'No visits yet';
  }

  private loadPatients(): void {
    this.loading.set(true);
    this.error.set(null);
    this.patientsService
      .getAllPatients()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (patients) => {
          this.patients.set((patients ?? []).map((p) => this.normalizePatientRow(p)));
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Failed to load patients. Please try again.');
          this.loading.set(false);
        },
      });
  }

  private lastVisitTime(patient: PatientResponse): number {
    const parsed = this.parseDate(patient.lastVisit);
    return parsed?.getTime() ?? 0;
  }

  private dayKey(patient: PatientResponse): string {
    const parsed = this.parseDate(patient.lastVisit);
    if (!parsed) {
      return 'No visits yet';
    }
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  private parseDate(value: string | undefined | null): Date | null {
    if (value == null) {
      return null;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (ymd) {
      const date = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatDateMdYyyy(dateString: string): string {
    const parsed = this.parseDate(dateString);
    if (!parsed) {
      return '—';
    }
    const mm = (parsed.getMonth() + 1).toString().padStart(2, '0');
    const dd = parsed.getDate().toString().padStart(2, '0');
    return `${mm}/${dd}/${parsed.getFullYear()}`;
  }

  private normalizePatientRow(raw: PatientResponse): PatientResponse {
    const r = raw as unknown as Record<string, unknown>;
    const dob = this.coerceApiDateOnly(r['dateOfBirth'] ?? r['date_of_birth']);
    const lastVisit = this.coerceApiDateOnly(r['lastVisit'] ?? r['last_visit']);
    return {
      ...raw,
      dateOfBirth: dob ?? '',
      lastVisit,
    };
  }

  private coerceApiDateOnly(value: unknown): string | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    if (Array.isArray(value) && value.length >= 3) {
      const y = Number(value[0]);
      const m = Number(value[1]);
      const d = Number(value[2]);
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
        return undefined;
      }
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    if (typeof value === 'string') {
      const s = value.trim();
      return s ? s.slice(0, 10) : undefined;
    }
    return undefined;
  }
}
